import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import VerifyGuide from './VerifyGuide';
import { BotAvatar, LivePet, SectionHeading, VISUALS } from './GuideVisuals';

// #discord: a short, friendly guide to CHΞΞKSCORD (replacing the long docs at cheeks.uk/docs).
// The top is the docs' header (TOGΞ's welcome message and the server card, live from the bot), then a menu that stays
// at the top while scrolling. Each section is one list, and each feature is one line: tap it for the details and an
// example of what TOGΞ posts.

const SERVER = 'https://discord.com/channels/1466528743265931459';
const HELLO = `${SERVER}/1466528744943648800`;
const JOIN_CODE = '8888';
const JOIN_URL = 'https://discord.com/invite/fs45z3r6Xe';

// Written to show off what TOGΞ does. Everything here is real-time as far as members are concerned: no sync
// intervals mentioned anywhere.
const SECTIONS = [
  { id: 'get-started', title: 'Get started', items: [
    { img: '/ticket.png', name: 'Help & tickets', visual: 'ticket', wide: true, text: 'Questions, suggestions or requests, handled privately in a ticket.',
      points: ['A private channel opened just for you', 'A full transcript sent to your DMs when it’s closed', 'Every ticket gets its own ID'],
      button: { label: 'Create Ticket', href: `${HELLO}/1469077255232946218`, color: '#da373c' } },
  ]},
  { id: 'marketplace', title: 'Marketplace', items: [
    { img: '/wnlogo.png', name: 'Whatnot', wide: true, visual: 'whatnot', text: 'CHΞΞKSCORD is fully synced with Whatnot, all run by TOGΞ. Verify as a seller and your streams, shows and shop are shared with the whole server.',
      points: [
        'Live alerts the moment you go live, with a live snapshot of your stream',
        'Live viewer count, max viewers and time live on every alert',
        'A stream recap when you end: max viewers and how long you streamed',
        'Today’s schedule of every member’s upcoming shows',
        'New listing alerts and a live shop board',
        'A “Live Now” counter in the channel list',
        'Profile cards: type `?profile` to see your Whatnot stats as an image',
        'Live now, schedules and shops mirrored on cheeks.uk',
      ] },
    { icon: '🛍️', name: 'eBay', text: 'TOGΞ posts your eBay listings for the whole server to see.',
      points: ['Link your eBay with Linked Roles', 'Trusted sellers only: 100% feedback and 3+ months selling'] },
  ]},
  { id: 'stock-alerts', title: 'Stock alerts', items: [
    { icon: '🔔', name: 'Restock alerts', wide: true, visual: 'restock', text: 'TOGΞ watches the big UK retailers around the clock and pings you the second stock lands.',
      points: ['A self-built monitor: far less competition than free or paid alert groups', 'Choose everything, or only the sets you want',
        'Set roles like Destined Rivals, Prismatic Evolutions and 30th Celebrations', 'Amazon invite-only products posted as they appear',
        'Pokémon Center queue alerts the moment a queue opens', 'Members’ wins shared in the success channel'],
      tags: ['Argos', 'Amazon', 'Smyths', 'HMV', 'Zavvi', 'Sports Direct', 'Pokémon Center'] },
  ]},
  { id: 'tcg-tools', title: 'TCG tools', items: [
    { icon: '🔎', name: 'Card search', text: 'Look up any card and TOGΞ brings back its details in seconds.' },
    { icon: '💰', name: 'Comp checker', text: 'Find the last sold price of any item before you buy or sell.' },
    { icon: '🏅', name: 'Slab cert check', wide: true, visual: 'cert', text: 'Check any graded slab against the grading company’s own records.',
      points: ['Pick the grader, enter the cert number', 'A private link straight to the official cert and pop report', 'Copy and paste it any way: spaces are tidied for you'],
      tags: ['PSA', 'BGS', 'CGC', 'SGC', 'TAG', 'ACE', 'AGS', 'GG'] },
  ]},
  { id: 'social-feeds', title: 'Social feeds', nav: 'Socials', items: [
    { icon: '📰', name: 'News & leaks', text: 'Breaking Pokémon TCG news and leaks, the moment they drop.', tags: ['PokéBeach'] },
    { icon: '🍿', name: 'YouTube', text: 'New uploads from the biggest Pokémon TCG creators, posted as they go up.',
      points: ['Rattle, PokeRev, Deep Pocket Monster, Leonhart and more', 'Got a channel? Link it with Linked Roles and your videos are shared too'] },
    { icon: '🕹️', name: 'Twitch', text: 'Members’ streams posted the moment they go live.',
      points: ['Link your Twitch with Linked Roles to share your streams with the server'] },
    { icon: '📝', name: 'Reddit', text: 'The best deals and discussions from Pokémon TCG Reddit, straight into Discord.',
      tags: ['r/PokemonDealsUK', 'r/PokemonTCGUK'] },
    { icon: '📸', name: 'Instagram', text: 'New Instagram posts from the community, shared with the whole server as they go up.' },
  ]},
  { id: 'activities', title: 'Activities', nav: 'Games', items: [
    { icon: '⚔️', name: 'Battle Arena', banner: '/arena.png', text: 'Knockout battles right inside Discord, Super Smash Bros style.',
      points: ['Go head-to-head with other members live', 'Last battler standing wins', 'Members only'],
      button: { label: 'Let’s battle!', href: 'https://discord.com/channels/1466528743265931459/1567420466560368680', color: '#5865f2' } },
    { icon: '🐣', name: 'Tamagotcheeks', visual: 'pet', text: 'The whole server raises one Togepi together.',
      points: ['Feed, play, pet, bath and nap buttons', 'Keep it happy and it evolves into Togetic, then Togekiss',
        'Its mood shows right in the channel list', 'Caretaker leaderboard, and live stats on cheeks.uk'] },
    { icon: '🔤', name: 'Hangman', visual: 'hangman', text: 'Race everyone to guess the hidden Pokémon, letter by letter.',
      points: ['Every Pokémon from every generation', 'Points for every right letter, a bonus for finishing the word', 'All-time leaderboard, and anyone can start the next game'] },
    { icon: '🏃', name: 'Cheeky Dash', banner: '/gamebanner_cheekydash.jpg', text: 'Get Togepi past the obstacles, right inside Discord.',
      points: ['Top scores on a live leaderboard in Discord', 'Shown under your server name'],
      button: { label: 'Play now', href: 'https://discord.com/channels/1466528743265931459/1567250237553123451', color: '#5865f2' } },
    { icon: '🎒', name: 'Pokétwo', text: 'Wild Pokémon spawn right in chat for you to catch and collect.',
      points: ['Get started with `@Pokétwo start`', 'Pick your starter with `@Pokétwo pick <pokemon>`', 'Catch with `@Pokétwo c <pokemon>`'] },
    { icon: '🧠', name: 'Words', text: 'Daily word puzzles: take on the rest of the server every day.',
      tags: ['Wordle', 'Daily Word Griddle', 'Daily Word Wheel'] },
  ]},
  { id: 'pokemon-go', title: 'Pokémon GO', items: [
    { icon: '🗞️', name: 'GO news', text: 'Pokémon GO news and leaks from Pokémon GO, GO Hub and Leek Duck, the moment they’re out.' },
    { icon: '🎁', name: 'Gifts', text: 'Grow your friend list and swap gifts with members.',
      points: ['Tap “Add My Trainer Code” and TOGΞ makes your code a scannable QR code', 'Friends just scan it in-game to add you'] },
  ]},
  { id: 'community', title: 'Community', items: [
    { icon: '🎉', name: 'Giveaways', visual: 'giveaway', text: 'Free giveaways for members: Pokémon TCG products, merch, collectibles and accessories.' },
    { icon: '📲', name: 'App updates', text: 'App and game updates posted for download before they’ve even hit the app store.', tags: ['Pocket TCG', 'Pokémon GO', 'Whatnot', 'eBay'] },
    { icon: '📅', name: 'Events calendar', text: 'Every TCG card show in the UK and US, all in one place.' },
    { icon: '✈️', name: 'Import guide', text: 'Simple guides to ordering from the US, Japan and more, with ease.' },
  ]},
];

// Text with `commands` in backticks: those parts are shown as inline code, the way Discord shows them.
// Every TOGΞ is shown as an @TOGΞ mention, like in the welcome message.
const withMentions = (text, key) => text.split('TOGΞ').flatMap((part, j) => (j
  ? [<Mention key={`${key}-${j}`}>@TOGΞ</Mention>, part]
  : [part]));
const withCode = (text) => text.split('`').map((part, i) => (i % 2
  ? <code key={i} className="rounded whitespace-nowrap" style={{ padding: '1px 4px', fontSize: '0.92em', background: '#1e1f22', color: '#dbdee1', fontFamily: 'ui-monospace, Consolas, Menlo, monospace' }}>{part}</code>
  : withMentions(part, i)));

// Small Discord-style pieces
const Tag = ({ children }) => (
  <span className="font-bold tracking-wide" style={{ fontSize: '0.55rem', lineHeight: 1, padding: '3px 5px', borderRadius: '4px', background: '#5865f2', color: '#ffffff' }}>{children}</span>
);
const Mention = ({ children }) => (
  <span className="rounded font-medium" style={{ padding: '0 3px', background: 'rgba(88,101,242,0.3)', color: '#c9cdfb' }}>{children}</span>
);

// The panel each section's list sits in: plain Discord grey, like the server card
const PANEL = { background: '#27272f' };

// One feature: a single line (icon, name, what it is). If there's more to it, tapping opens the details and the example
function FeatureRow({ item }) {
  const [open, setOpen] = useState(false);
  const Visual = VISUALS[item.visual];
  const more = !!(item.points || item.tags || item.button || item.link || item.banner || Visual);
  const icon = (
    <span className="flex-shrink-0 flex items-center justify-center rounded-xl" style={{ width: '36px', height: '36px', background: 'rgba(88,101,242,0.15)' }}>
      {item.img
        ? (item.round
          ? <img src={item.img} alt="" className="rounded-full object-cover" style={{ width: '30px', height: '30px' }} />
          : <img src={item.img} alt="" className="object-contain" style={{ width: '22px', height: '22px' }} />)
        : <span style={{ fontSize: '1.1rem', lineHeight: 1 }} aria-hidden="true">{item.icon}</span>}
    </span>
  );
  const heading = (
    <span className="flex-1 min-w-0">
      <span className="block text-white font-semibold" style={{ fontSize: '0.85rem', lineHeight: 1.25 }}>{item.name}</span>
      <span className="block text-zinc-400 mt-0.5" style={{ fontSize: '0.72rem', lineHeight: 1.4 }}>{withCode(item.text)}</span>
    </span>
  );
  return (
    <li>
      {more ? (
        <button onClick={() => setOpen(!open)} aria-expanded={open}
          className="w-full flex items-center gap-3 text-left px-3 py-3 transition-colors hover:bg-white/[0.03]">
          {icon}{heading}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            className="flex-shrink-0 transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'none' }} aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      ) : (
        <div className="flex items-center gap-3 px-3 py-3">{icon}{heading}</div>
      )}
      {open && <FeatureDetails item={item} Visual={Visual} />}
    </li>
  );
}

function FeatureDetails({ item, Visual }) {
  return (
    // Lined up under the name, past the icon
    <div className="flex flex-col gap-2.5 pb-3 pr-3" style={{ paddingLeft: '60px' }}>
      {item.banner && <img src={item.banner} alt="" loading="lazy" className="block w-full rounded-xl object-cover" style={{ aspectRatio: '16 / 7' }} />}
        {item.points && (
          <ul className={`grid gap-x-4 gap-y-1 ${item.wide ? 'sm:grid-cols-2' : ''}`}>
            {item.points.map((point) => (
              <li key={point} className="flex items-start gap-1.5 text-zinc-300" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" style={{ marginTop: '2px' }} aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                <span>{withCode(point)}</span>
              </li>
            ))}
          </ul>
        )}
        {item.tags && item.points && (
          // Under a checklist, the tags are rounded pills so the two lists don't look the same
          <div className="flex flex-wrap gap-1">
            {item.tags.map((t) => (
              <span key={t} className="rounded-full font-medium text-zinc-200" style={{ fontSize: '0.62rem', lineHeight: 1.2, padding: '3px 8px', background: 'rgba(88,101,242,0.18)', border: '1px solid rgba(88,101,242,0.35)' }}>{t}</span>
            ))}
          </div>
        )}
        {item.tags && !item.points && (
          // On their own, the tags are a checklist, in columns so the longer lists stay compact
          <ul className={`grid grid-cols-2 gap-x-4 gap-y-1 ${item.wide ? 'sm:grid-cols-4' : ''}`}>
            {item.tags.map((t) => (
              <li key={t} className="flex items-start gap-1.5 text-zinc-300" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" style={{ marginTop: '2px' }} aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        )}
        {(item.button || item.link) && (
          <div className="flex flex-wrap items-center gap-1.5">
            {item.button && (
              <a href={item.button.href} target="_blank" rel="noopener noreferrer" className="rounded font-semibold text-white hover:brightness-110 active:scale-95 transition-all"
                style={{ fontSize: '0.68rem', lineHeight: 1.2, padding: '4px 10px', background: item.button.color }}>{item.button.label}</a>
            )}
            {item.link && (
              <a href={item.link.href} className="rounded font-semibold text-white hover:brightness-110 active:scale-95 transition-all"
                style={{ fontSize: '0.68rem', lineHeight: 1.2, padding: '4px 10px', background: '#5865f2' }}>{item.link.label}</a>
            )}
          </div>
        )}
      {Visual && <Visual />}
    </div>
  );
}

// The menu that stays at the top while scrolling: one button per section, the one you're reading lit up
function GuideNav({ sections }) {
  const [active, setActive] = useState(sections[0].id);
  const bar = useRef(null);

  useEffect(() => {
    const seen = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => seen.set(e.target.id, e.isIntersecting));
      const first = sections.find((s) => seen.get(s.id));
      if (first) setActive(first.id);
    }, { rootMargin: '-70px 0px -60% 0px' }); // the section nearest the top of the screen, just under the menu
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [sections]);

  // Keep the lit-up button in view on a phone, where the menu scrolls sideways (only the menu: never the page)
  useEffect(() => {
    const button = bar.current?.querySelector(`[data-id="${active}"]`);
    if (button) bar.current.scrollTo({ left: button.offsetLeft - bar.current.clientWidth / 2 + button.offsetWidth / 2, behavior: 'smooth' });
  }, [active]);

  return (
    <nav className="sticky top-0 z-40 -mx-4 px-4 py-2" style={{ background: 'rgba(17,17,20,0.88)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }} aria-label="Guide sections">
      {/* Scrolls sideways on a phone; wraps onto a second line on a bigger screen, where there's no swiping */}
      <div ref={bar} className="flex gap-1.5 overflow-x-auto sm:flex-wrap guide-nav">
        {sections.map((s) => (
          <button key={s.id} data-id={s.id} onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="flex-shrink-0 rounded-full font-semibold whitespace-nowrap transition-colors"
            style={{ fontSize: '0.72rem', lineHeight: 1, padding: '7px 12px', background: active === s.id ? '#5865f2' : 'rgba(255,255,255,0.06)', color: active === s.id ? '#ffffff' : '#b5bac1' }}
            aria-current={active === s.id ? 'true' : undefined}>
            {s.nav || s.title}
          </button>
        ))}
      </div>
    </nav>
  );
}

// The partner offer, last: it's for other servers' owners, not members
const PARTNER_ID = 'toge-for-your-server';
const NAV = [
  { id: 'how-to-verify', title: 'Verify' },
  ...SECTIONS,
  { id: PARTNER_ID, title: 'TOGΞ for your server', nav: 'For your server' },
];

// TOGΞ's welcome message reactions: unpressed until you tap one; your taps are remembered in this browser.
// (Its own keys, so reactions from the old docs page, also on cheeks.uk, don't carry over.)
function Reaction({ id, base, children }) {
  const key = `guide-reaction-${id}`;
  const [reacted, setReacted] = useState(() => { try { return localStorage.getItem(key) === '1'; } catch (e) { return false; } });
  const toggle = () => {
    const next = !reacted;
    setReacted(next);
    try { if (next) localStorage.setItem(key, '1'); else localStorage.removeItem(key); } catch (e) {}
  };
  return (
    <button onClick={toggle} className="flex items-center gap-1 rounded-lg transition-colors" aria-pressed={reacted}
      style={{ padding: '2px 7px', fontSize: '0.75rem', background: reacted ? 'rgba(88,101,242,0.25)' : 'rgba(255,255,255,0.06)', border: `1px solid ${reacted ? '#5865f2' : 'transparent'}`, color: '#dbdee1' }}>
      {children}<span className="font-semibold">{base + (reacted ? 1 : 0)}</span>
    </button>
  );
}

export default function DiscordGuide({ database }) {
  const [stats, setStats] = useState({});
  const [joinOpen, setJoinOpen] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [copied, setCopied] = useState(false);

  // Live server numbers and avatars, the same ones the bot keeps up to date for the homepage and docs, and the server
  // Togepi's stats, as shown in the Arcade
  useEffect(() => {
    const keys = ['memberCount', 'onlineCount', 'voiceCount', 'discordBoosts', 'discordAvatars', 'togepi'];
    const offs = keys.map((k) => onValue(ref(database, k), (snap) => setStats((s) => ({ ...s, [k]: snap.val() })), () => {}));
    return () => offs.forEach((off) => off());
  }, [database]);

  const num = (v) => (typeof v === 'number' ? v.toLocaleString() : '…');
  const avatars = stats.discordAvatars || {};
  const boosts = stats.discordBoosts || {};

  const submitCode = () => {
    if (code.trim() === JOIN_CODE) window.location.href = JOIN_URL;
    else { setCodeError('Incorrect code. Please try again.'); setCode(''); }
  };
  const copyUrl = () => {
    navigator.clipboard?.writeText('discord.cheeks.uk').catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    // relative: keeps everything on the page above the Discord-slate background behind it.
    // The whole page is in Poppins (like the docs), not the site's brand font
    <main className="flex-1 relative discord-guide" style={{ fontFamily: "'Poppins', 'gg sans', 'Noto Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        .discord-guide h1, .discord-guide h2, .discord-guide h3, .discord-guide button, .discord-guide input { font-family: inherit; }
        .guide-nav { scrollbar-width: none; }
        .guide-nav::-webkit-scrollbar { display: none; }
      `}</style>
      <div className="max-w-2xl mx-auto px-4 pt-2 pb-10 flex flex-col gap-4">

        {/* Server card: the welcome banner, live icon and numbers, and the way in */}
        <div className="relative rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.35)]" style={{ background: '#27272f' }}>
          {/* The whole banner, at its own shape (2.36:1), so nothing is cropped */}
          <div style={{ aspectRatio: '2.36 / 1', width: '100%', background: '#5865f2 url(/welcome.gif) center / cover no-repeat' }} />
          <div className="flex flex-col items-center text-center px-5 pb-5" style={{ marginTop: '-44px' }}>
            <div className="relative">
              {avatars.server
                ? <img src={avatars.server} alt="CHΞΞKSCORD" className="rounded-full object-cover" style={{ width: '84px', height: '84px', border: '4px solid #27272f', background: '#2c2f33' }} />
                : <span className="block rounded-full" style={{ width: '84px', height: '84px', border: '4px solid #27272f', background: '#2c2f33' }} />}
              <span className="absolute rounded-full" style={{ width: '18px', height: '18px', right: '4px', bottom: '4px', background: '#23a55a', border: '3px solid #27272f' }} />
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <h1 className="text-white font-bold" style={{ fontSize: '1.15rem' }}>CHΞΞKSCORD</h1>
              <img src="/community.png" alt="" style={{ width: '18px', height: '18px' }} />
              <Tag>SΞRVΞR</Tag>
            </div>
            <div className="text-zinc-400" style={{ fontSize: '0.72rem' }}>{num(stats.memberCount)} Members • Community</div>
            <div className="flex flex-wrap justify-center gap-1.5 mt-3">
              <span className="flex items-center gap-1.5 rounded-full text-zinc-400" style={{ fontSize: '0.68rem', padding: '5px 10px', background: 'rgba(88,101,242,0.18)' }}>
                <span className="rounded-full" style={{ width: '7px', height: '7px', background: '#23a55a', boxShadow: '0 0 6px rgba(35,165,90,0.7)' }} />
                <b className="text-white">{num(stats.onlineCount)}</b> Online
              </span>
              <span className="flex items-center gap-1.5 rounded-full text-zinc-400" style={{ fontSize: '0.68rem', padding: '5px 10px', background: 'rgba(88,101,242,0.18)' }}>
                <svg width="10" height="10" viewBox="0.5 3.5 17 17" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                <b className="text-white">{num(stats.voiceCount)}</b> In voice
              </span>
              {typeof boosts.count === 'number' && (
                <span className="flex items-center gap-1 rounded-full text-zinc-400" style={{ fontSize: '0.68rem', padding: '5px 10px', background: 'rgba(88,101,242,0.18)' }}>
                  <img src="/boost.png" alt="" style={{ width: '14px', height: '14px', margin: '-2px 0' }} />
                  <b className="text-white">{num(boosts.count)}</b> Boosts · Lvl <b className="text-white">{boosts.tier ?? '…'}</b>
                </span>
              )}
            </div>
            <button onClick={() => { setJoinOpen(true); setCode(''); setCodeError(''); }}
              className="mt-4 w-full max-w-xs flex items-center justify-center gap-2 rounded-full text-white font-semibold active:scale-95 transition-all"
              style={{ padding: '9px 16px', fontSize: '0.8rem', background: 'linear-gradient(135deg, #5865F2, #4752C4)' }}>
              <img src="/discord.png" alt="" style={{ width: '18px', height: '18px' }} />
              Join CHΞΞKSCORD
            </button>
            <button onClick={copyUrl} className="mt-3 flex items-center gap-2 font-semibold uppercase text-zinc-500 hover:text-zinc-300 transition-colors"
              style={{ fontSize: '0.68rem', letterSpacing: '0.3em' }} aria-label="Copy discord.cheeks.uk">
              discord.cheeks.uk
              <span style={{ letterSpacing: 0, color: copied ? '#23a55a' : undefined }}>{copied ? '✓' : '⧉'}</span>
            </button>
          </div>
        </div>

        {/* TOGΞ's welcome message, under the server card, highlighted like a message that mentions you */}
        <div className="flex gap-3 rounded-r-xl" style={{ background: 'rgba(234,152,0,0.11)', borderLeft: '4px solid #ea9800', padding: '11px 14px 11px 10px' }}>
          {avatars.bot
            ? <img src={avatars.bot} alt="TOGΞ" className="rounded-full flex-shrink-0 object-cover" style={{ width: '40px', height: '40px' }} />
            : <span className="rounded-full flex-shrink-0" style={{ width: '40px', height: '40px', background: '#2c2f33' }} />}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-white font-semibold" style={{ fontSize: '0.9rem' }}>TOGΞ</span>
              <Tag>APP</Tag>
              <span className="text-zinc-500" style={{ fontSize: '0.65rem' }}>03:33</span>
            </div>
            <p className="mt-1" style={{ fontSize: '0.8rem', lineHeight: 1.45, color: '#dbdee1' }}>
              Welcome <Mention>@everyone</Mention>!<br />
              <strong className="text-white">CHΞΞKSCORD</strong> brings together restock alerts, TCG tools, social integrations and community
              activities, all powered by <Mention>@TOGΞ</Mention>. Here’s everything on offer.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Reaction id="heart" base={272}>❤️</Reaction>
              <Reaction id="100" base={192}>💯</Reaction>
              <Reaction id="diamond" base={138}><img src="/diamond.png" alt="" style={{ width: '16px', height: '16px' }} /></Reaction>
            </div>
          </div>
        </div>

        <GuideNav sections={NAV} />

        {/* New members arrive from the welcome message's Guide button, so verifying comes first */}
        <VerifyGuide verifyUrl={`${HELLO}/1469077227042902018`} />

        {/* Every feature: one list per section */}
        <BotAvatar.Provider value={avatars.bot}>
        <LivePet.Provider value={stats.togepi && typeof stats.togepi === 'object' ? stats.togepi : null}>
        {SECTIONS.map((section) => (
          <section key={section.id} id={section.id} className="flex flex-col gap-2.5" style={{ scrollMarginTop: '64px' }}>
            <SectionHeading>{section.title}</SectionHeading>
            <ul className="rounded-2xl overflow-hidden divide-y divide-white/[0.06] shadow-[0_4px_16px_rgba(0,0,0,0.35)]" style={PANEL}>
              {section.items.map((item) => <FeatureRow key={item.name} item={item} />)}
            </ul>
          </section>
        ))}
        </LivePet.Provider>
        </BotAvatar.Provider>

        {/* TOGΞ in your own server (partners): linked to directly from the homepage banner */}
        <section id={PARTNER_ID} className="flex flex-col gap-2.5" style={{ scrollMarginTop: '64px' }}>
          <SectionHeading>TOGΞ for your server</SectionHeading>
          <ul className="rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.35)]" style={PANEL}>
            <FeatureRow item={{
              img: avatars.bot || '/pball.svg', round: !!avatars.bot, name: 'Add TOGΞ to your Discord server', wide: true,
              text: 'Now available for CHΞΞKSCORD partners. Your Whatnot, Twitch and YouTube alerts, posted in your own server the moment you go live, upload, or add products to your store.',
              points: [
                'Whatnot live alerts with a live snapshot, viewers, max views and time live',
                'New product alerts when you add Buy It Now items to your store',
                'Twitch go-live alerts with a live preview, kept up to date while you stream',
                'New YouTube videos and livestreams, with Discord’s own player',
                'Pick the channels and roles to ping from your own dashboard, in seconds',
                'No commands and nothing to set up in Discord: TOGΞ only posts your alerts',
              ],
              tags: ['Whatnot', 'Twitch', 'YouTube'],
              link: { label: 'Open Partner Dashboard', href: '/#partner' },
              button: { label: 'Ask to become a partner', href: `${HELLO}/1469077255232946218`, color: '#da373c' },
            }} />
          </ul>
        </section>
      </div>

      {/* Invite-only: the access code, as on the docs */}
      {joinOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={() => setJoinOpen(false)}>
          <div className="rounded-2xl text-center shadow-2xl w-full p-6" style={{ background: '#27272f', maxWidth: '320px' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold" style={{ fontSize: '1.05rem' }}>Enter Access Code</h3>
            <p className="mt-1" style={{ fontSize: '0.72rem', color: '#ed4245' }}>CHΞΞKSCORD is currently invite only</p>
            <p className="text-zinc-400 mt-1" style={{ fontSize: '0.75rem' }}>Enter the 4-digit code to join CHΞΞKSCORD.</p>
            <label htmlFor="join-code" className="sr-only">Access code</label>
            <input id="join-code" autoFocus inputMode="numeric" maxLength={4} value={code} placeholder="····"
              onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submitCode(); if (e.key === 'Escape') setJoinOpen(false); }}
              className="mt-4 w-full rounded-lg text-center text-white outline-none"
              style={{ padding: '10px', fontSize: '1.4rem', letterSpacing: '0.5em', background: '#1c1d22', border: '2px solid #5865f2' }} />
            {codeError && <p className="mt-2" style={{ fontSize: '0.75rem', color: '#ed4245' }}>{codeError}</p>}
            <button onClick={submitCode} className="mt-3 w-full rounded-full text-white font-semibold active:scale-95 transition-all"
              style={{ padding: '9px', fontSize: '0.8rem', background: 'linear-gradient(135deg, #5865F2, #4752C4)' }}>Continue</button>
          </div>
        </div>
      )}
    </main>
  );
}
